import { useEffect } from 'react'
import { Portal } from '../Portal'
import { cn } from '../../lib/cn'
import { headerCtas, platformMenu, primaryNav } from '../../lib/site-nav'

export function MobileNavDrawer({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  return (
    <Portal>
      {/* `inert` when closed: below lg the drawer is only translated off-screen
          (not display:none), so without it the links inside would stay
          keyboard-focusable inside an aria-hidden subtree, which breaks the
          accessibility tree for screen readers and browsing agents alike. */}
      <div
        aria-hidden={!open}
        inert={!open}
        className={cn(
          'fixed inset-0 z-[60] lg:hidden',
          open ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className={cn(
            'absolute inset-0 h-full w-full bg-ink-950/40 backdrop-blur-sm transition-opacity duration-300',
            open ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className={cn(
            'absolute inset-x-0 top-0 max-h-[100dvh] overflow-y-auto rounded-b-3xl border-b border-ink-100 bg-paper shadow-2xl transition-transform duration-300 ease-out',
            open ? 'translate-y-0' : '-translate-y-full',
          )}
        >
          <div className="container-page flex h-16 items-center justify-between">
            <img src="/logo-black.svg" alt="LangWatch" className="h-6 w-auto" />
            <button
              type="button"
              aria-label="Close menu"
              onClick={onClose}
              className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 hover:bg-ink-100 hover:text-ink-950"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="container-page pb-8 pt-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500">
              Platform
            </div>
            <ul className="mt-2 divide-y divide-ink-100">
              {platformMenu.map((p) => {
                const Icon = p.icon
                return (
                  <li key={p.label}>
                    <a
                      href={p.href}
                      onClick={onClose}
                      className="flex items-start gap-3 py-3"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ink-900 text-paper">
                        {p.iconSrc ? (
                          <img
                            src={p.iconSrc}
                            alt=""
                            aria-hidden
                            className="h-[20px] w-[20px] object-contain"
                          />
                        ) : (
                          Icon && <Icon className="h-[18px] w-[18px]" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-[14px] font-semibold text-ink-900">
                          {p.label}
                          {p.badge && (
                            <span className="rounded-full bg-brand-500/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-brand-700">
                              {p.badge}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-[12.5px] leading-snug text-ink-500">
                          {p.desc}
                        </span>
                      </span>
                    </a>
                  </li>
                )
              })}
            </ul>

            <div className="mt-6 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500">
              More
            </div>
            <ul className="mt-1 divide-y divide-ink-100">
              {primaryNav
                .filter((item) => item.label !== 'Platform')
                .map((item) => {
                  const ext = item.external
                    ? { target: '_blank', rel: 'noreferrer' as const }
                    : {}
                  return (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        {...ext}
                        onClick={onClose}
                        className="block py-3 text-[15px] font-medium text-ink-900"
                      >
                        {item.label}
                      </a>
                    </li>
                  )
                })}
            </ul>

            <div className="mt-8 grid gap-2">
              <a
                href={headerCtas.signUp.href}
                target="_blank"
                rel="noreferrer"
                onClick={onClose}
                className="btn-primary w-full justify-center !py-2.5 !text-[14px]"
              >
                Sign Up
              </a>
              <a
                href={headerCtas.contactSales.href}
                onClick={onClose}
                className="btn-secondary w-full justify-center !py-2.5 !text-[14px]"
              >
                {headerCtas.contactSales.label}
              </a>
              <a
                href={headerCtas.signIn.href}
                target="_blank"
                rel="noreferrer"
                onClick={onClose}
                className="mt-1 text-center text-[13.5px] font-medium text-ink-600"
              >
                Sign in
              </a>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
